import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTheme } from '@/contexts/ThemeContext';

// Mock products data (since API hooks aren't working yet)
const MOCK_PRODUCTS = [
  {
    productId: '1',
    name: 'Vintage Overdrive',
    description: 'Classic tube overdrive sound with warm, natural breakup',
    price: 4999,
    category: 'pedal',
    imageUrl: null
  },
  {
    productId: '2',
    name: 'Studio Drum Kit',
    description: 'Professional drum samples recorded in world-class studios',
    price: 9999,
    category: 'sample',
    imageUrl: null
  },
  {
    productId: '3',
    name: 'Synthwave Presets',
    description: '80s-inspired synth presets for modern production',
    price: 2999,
    category: 'preset',
    imageUrl: null
  },
  {
    productId: '4',
    name: 'Analog Delay',
    description: 'Warm tape-style delay with modulation',
    price: 5999,
    category: 'pedal',
    imageUrl: null
  },
  {
    productId: '5',
    name: 'Cinematic Strings',
    description: 'Orchestral string samples for film scoring',
    price: 14999,
    category: 'sample',
    imageUrl: null
  },
  {
    productId: '6',
    name: 'Lo-Fi Hip Hop Pack',
    description: 'Chill beats and lo-fi presets for hip hop production',
    price: 3999,
    category: 'preset',
    imageUrl: null
  }
];

const Marketplace = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState<'pedal' | 'sample' | 'preset' | undefined>();

  const filteredProducts = selectedCategory
    ? MOCK_PRODUCTS.filter(p => p.category === selectedCategory)
    : MOCK_PRODUCTS;

  const colors = {
    dark: {
      bg: '#0a0a0f',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      text: '#ffffff',
      textMuted: '#9ca3af',
      hover: '#2d2d44'
    },
    light: {
      bg: '#f9fafb',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280',
      hover: '#f3f4f6'
    }
  };

  const c = colors[theme];

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'pedal':
        return '#667eea';
      case 'sample':
        return '#f093fb';
      case 'preset':
        return '#4facfe';
      default:
        return '#6b7280';
    }
  };

  const getCategoryEmoji = (category: string) => {
    switch (category) {
      case 'pedal':
        return '🎸';
      case 'sample':
        return '🎵';
      case 'preset':
        return '🎨';
      default:
        return '🎵';
    }
  };

  return (
    <div style={{ backgroundColor: c.bg, minHeight: '89.7vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
        {/* Header */}
        <div style={{ marginBottom: '3rem' }}>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 'bold',
            color: c.text,
            marginBottom: '0.5rem'
          }}>
            Marketplace
          </h1>
          <p style={{ fontSize: '1.125rem', color: c.textMuted }}>
            Discover premium audio tools from professional creators
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr', gap: '2rem' }}>
          {/* Sidebar */}
          <aside>
            <div style={{
              background: c.cardBg,
              borderRadius: '1rem',
              padding: '1.5rem',
              border: `1px solid ${c.cardBorder}`,
              backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
              boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{
                fontSize: '1.125rem',
                fontWeight: '600',
                color: c.text,
                marginBottom: '1rem'
              }}>
                Categories
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <CategoryButton
                  label="All Products"
                  active={!selectedCategory}
                  onClick={() => setSelectedCategory(undefined)}
                  theme={theme}
                />
                <CategoryButton
                  label="Pedals"
                  active={selectedCategory === 'pedal'}
                  onClick={() => setSelectedCategory('pedal')}
                  theme={theme}
                />
                <CategoryButton
                  label="Samples"
                  active={selectedCategory === 'sample'}
                  onClick={() => setSelectedCategory('sample')}
                  theme={theme}
                />
                <CategoryButton
                  label="Presets"
                  active={selectedCategory === 'preset'}
                  onClick={() => setSelectedCategory('preset')}
                  theme={theme}
                />
              </div>
            </div>

            {/* Stats Card */}
            <div style={{
              background: c.cardBg,
              borderRadius: '1rem',
              padding: '1.5rem',
              border: `1px solid ${c.cardBorder}`,
              backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
              boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
              marginTop: '1rem'
            }}>
              <h4 style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: c.textMuted,
                textTransform: 'uppercase',
                marginBottom: '1rem',
                letterSpacing: '0.05em'
              }}>
                Marketplace Stats
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <StatItem label="Products" value={MOCK_PRODUCTS.length.toString()} theme={theme} />
                <StatItem label="Creators" value="42" theme={theme} />
                <StatItem label="Downloads" value="5.2K" theme={theme} />
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem'
            }}>
              <div>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: c.text,
                  marginBottom: '0.25rem'
                }}>
                  {selectedCategory ? `${selectedCategory}s` : 'All Products'}
                </h2>
                <p style={{ fontSize: '0.875rem', color: c.textMuted }}>
                  {filteredProducts.length} products available
                </p>
              </div>
            </div>

            {/* Products Grid */}
            {filteredProducts.length > 0 ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '1.5rem'
              }}>
                {filteredProducts.map((product) => (
                  <ProductCard
                    key={product.productId}
                    product={product}
                    formatPrice={formatPrice}
                    getCategoryColor={getCategoryColor}
                    getCategoryEmoji={getCategoryEmoji}
                    onClick={() => navigate({ to: '/marketplace/$productId', params: { productId: product.productId } })}
                    theme={theme}
                  />
                ))}
              </div>
            ) : (
              <div style={{
                background: c.cardBg,
                borderRadius: '1rem',
                padding: '4rem 2rem',
                textAlign: 'center',
                border: `1px solid ${c.cardBorder}`,
                backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
                boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <p style={{ fontSize: '1.125rem', color: c.textMuted }}>
                  No products found in this category
                </p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

interface CategoryButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
  theme: 'dark' | 'light';
}

function CategoryButton({ label, active, onClick, theme }: CategoryButtonProps) {
  const hoverColor = theme === 'dark' ? '#2d2d44' : '#f3f4f6';

  return (
    <button
      onClick={onClick}
      style={{
        padding: '0.75rem 1rem',
        borderRadius: '0.5rem',
        border: 'none',
        backgroundColor: active ? '#667eea' : 'transparent',
        color: active ? 'white' : (theme === 'dark' ? '#9ca3af' : '#4b5563'),
        fontSize: '0.875rem',
        fontWeight: '500',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onMouseOver={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = hoverColor;
        }
      }}
      onMouseOut={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = 'transparent';
        }
      }}
    >
      {label}
    </button>
  );
}

interface StatItemProps {
  label: string;
  value: string;
  theme: 'dark' | 'light';
}

function StatItem({ label, value, theme }: StatItemProps) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: '0.875rem', color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>{label}</span>
      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: theme === 'dark' ? '#ffffff' : '#111827' }}>{value}</span>
    </div>
  );
}

interface ProductCardProps {
  product: typeof MOCK_PRODUCTS[0];
  formatPrice: (cents: number) => string;
  getCategoryColor: (category: string) => string;
  getCategoryEmoji: (category: string) => string;
  onClick: () => void;
  theme: 'dark' | 'light';
}

function ProductCard({ product, formatPrice, getCategoryColor, getCategoryEmoji, onClick, theme }: ProductCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: theme === 'dark'
          ? 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)'
          : '#ffffff',
        border: `1px solid ${theme === 'dark' ? 'rgba(102, 126, 234, 0.2)' : '#e5e7eb'}`,
        borderRadius: '1rem',
        overflow: 'hidden',
        backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
        boxShadow: isHovered ? '0 12px 24px rgba(0,0,0,0.15)' : (theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'),
        cursor: 'pointer',
        transition: 'all 0.2s',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)'
      }}
    >
      {/* Image/Icon */}
      <div style={{
        height: '200px',
        background: `linear-gradient(135deg, ${getCategoryColor(product.category)} 0%, ${getCategoryColor(product.category)}dd 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '4rem',
        position: 'relative'
      }}>
        {getCategoryEmoji(product.category)}
        <div style={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          backgroundColor: 'rgba(255,255,255,0.9)',
          padding: '0.25rem 0.75rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: '600',
          color: getCategoryColor(product.category),
          textTransform: 'uppercase'
        }}>
          {product.category}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '1.5rem' }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: '600',
          color: theme === 'dark' ? '#ffffff' : '#111827',
          marginBottom: '0.5rem'
        }}>
          {product.name}
        </h3>
        <p style={{
          fontSize: '0.875rem',
          color: theme === 'dark' ? '#9ca3af' : '#6b7280',
          lineHeight: '1.5',
          marginBottom: '1.5rem',
          minHeight: '2.5rem'
        }}>
          {product.description}
        </p>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: '1.75rem',
            fontWeight: 'bold',
            color: theme === 'dark' ? '#ffffff' : '#111827'
          }}>
            {formatPrice(product.price)}
          </span>
          <button style={{
            padding: '0.5rem 1.25rem',
            backgroundColor: getCategoryColor(product.category),
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'opacity 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseOut={(e) => e.currentTarget.style.opacity = '1'}>
            View Details
          </button>
        </div>
      </div>
    </div>
  );
}

export default Marketplace;
